import { safeLocal } from '../safeStorage';
import { eventHub } from './event-hub';
import type { EHConsent } from './event-tracker';
import {
  configureGa4,
  ensureGa4Script,
  ensureGtag,
  resetGa4ScriptPromise,
} from './ga4Client';

export const COOKIE_CONSENT_KEY = 'cookie_consent';
export type CookieConsentChoice = 'accepted' | 'rejected';

type ConsentAnalyticsHandler = () => void;

const pendingHandlers: ConsentAnalyticsHandler[] = [];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Google Consent Mode v2 — ad_storage + analytics_storage */
export function applyGoogleConsent(granted: boolean): void {
  if (typeof window === 'undefined') return;
  const gtag = ensureGtag();
  const state = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: state,
    analytics_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
  window.dataLayer?.push({
    event: granted ? 'cookie_consent_granted' : 'cookie_consent_denied',
  });
}

export function consentFromChoice(choice: CookieConsentChoice): EHConsent {
  const granted = choice === 'accepted';
  return {
    ads_storage: granted,
    analytics_storage: granted,
    ad_personalization: granted,
    meta_consent: granted,
    google_consent: granted,
    snapchat_consent: granted,
    tiktok_consent: granted,
    opt_out: !granted,
    npa: !granted,
  };
}

export function readCookieConsent(): CookieConsentChoice | null {
  try {
    const value = safeLocal.getItem(COOKIE_CONSENT_KEY);
    if (value === 'accepted' || value === 'rejected') return value;
  } catch {
    /* ignore */
  }
  return null;
}

export function hasAnalyticsConsent(): boolean {
  return readCookieConsent() === 'accepted';
}

/** تشغيل التتبع فقط بعد قبول الكوكيز */
export function runWhenAnalyticsConsented(handler: ConsentAnalyticsHandler): void {
  if (readCookieConsent() === 'accepted') {
    void ensureGa4Script().then(handler);
    return;
  }
  if (readCookieConsent() === 'rejected') return;
  pendingHandlers.push(handler);
}

function flushPendingHandlers(): void {
  const handlers = pendingHandlers.splice(0);
  handlers.forEach((handler) => handler());
}

function clearPendingHandlers(): void {
  pendingHandlers.length = 0;
}

async function notifyConsentGranted(): Promise<void> {
  applyGoogleConsent(true);
  eventHub.setConsent(consentFromChoice('accepted'));

  await ensureGa4Script();
  configureGa4();

  // مهلة لتطبيق Consent Mode قبل page_view
  await new Promise<void>((resolve) => window.setTimeout(resolve, 200));

  flushPendingHandlers();
}

export function applyCookieConsent(choice: CookieConsentChoice): void {
  try {
    safeLocal.setItem(COOKIE_CONSENT_KEY, choice);
  } catch {
    /* ignore */
  }

  if (choice === 'accepted') {
    void notifyConsentGranted();
    return;
  }

  applyGoogleConsent(false);
  eventHub.setConsent(consentFromChoice('rejected'));
  clearPendingHandlers();
  resetGa4ScriptPromise();
}

/** استعادة الموافقة المحفوظة عند تحميل التطبيق */
export function initCookieConsent(): void {
  const stored = readCookieConsent();
  if (!stored) return;
  applyGoogleConsent(stored === 'accepted');
  eventHub.setConsent(consentFromChoice(stored));
  if (stored === 'accepted') {
    void ensureGa4Script().then(() => configureGa4());
  }
}

export { ensureGa4Script, configureGa4, fireGa4PageView } from './ga4Client';
